import { useEffect, useState } from "react";
import { assetUrl } from "@/lib/api";
import { DFC_LAYOUTS, MULTI_FACE_LAYOUTS } from "@/lib/mtg";
import { emptyFace } from "@/lib/queries";
import { FaceFields } from "@/components/cards/CardFaceForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function ImagePool({ pool, selected, onSelect }) {
  return (
    <div className="grid max-h-64 grid-cols-5 gap-2 overflow-y-auto pr-1 sm:grid-cols-7" data-testid="bulk-back-image-pool">
      {pool.map((img) => (
        <button key={img.file_id} type="button" onClick={() => onSelect(img)} title={img.filename} aria-pressed={selected === img.file_id}
          className={`overflow-hidden rounded border-2 transition-colors ${selected === img.file_id ? "border-amber-400" : "border-transparent hover:border-amber-700"}`}
          data-testid={`bulk-pool-image-${img.file_id}`}>
          <img src={assetUrl(img.url)} alt={img.filename} className="aspect-[63/88] w-full object-cover" />
        </button>
      ))}
    </div>
  );
}

export function FaceDetailsDialog({ row, pool, onClose, onSave }) {
  const [draft, setDraft] = useState(row);
  useEffect(() => setDraft(row), [row]);
  if (!draft) return null;

  const multi = MULTI_FACE_LAYOUTS.includes(draft.layout);
  const dfc = DFC_LAYOUTS.includes(draft.layout);
  const faces = draft.faces.length > 1 ? draft.faces : [draft.faces[0], emptyFace()];
  const setFace = (i) => (patch) => setDraft((d) => {
    const fs = d.faces.length > 1 ? [...d.faces] : [d.faces[0], emptyFace()];
    fs[i] = { ...fs[i], ...patch };
    return { ...d, faces: fs };
  });
  const save = () => onSave({ ...draft, faces: multi ? faces : [faces[0]] });

  return (
    <Dialog open={Boolean(row)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-[#2A3447] bg-[#121620] text-slate-200" data-testid="bulk-details-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-amber-100">Card details</DialogTitle>
          <DialogDescription>Rules text, flavor, stats{multi ? " and the second face" : ""}. Change the layout in the grid to enable a back face.</DialogDescription>
        </DialogHeader>
        <div className="mb-4">
          <label className="field-label" htmlFor="bulk-collector">Collector number</label>
          <input id="bulk-collector" className="field w-40" value={draft.collector_number} onChange={(e) => setDraft({ ...draft, collector_number: e.target.value })} data-testid="bulk-details-collector" />
        </div>
        <Tabs defaultValue="front">
          <TabsList className="bg-[#0B0D12]">
            <TabsTrigger value="front" data-testid="bulk-details-front-tab">Front face</TabsTrigger>
            {multi && <TabsTrigger value="back" data-testid="bulk-details-back-tab">{dfc ? "Back face" : "Second half"}</TabsTrigger>}
          </TabsList>
          <TabsContent value="front" className="pt-4"><FaceFields face={faces[0]} onChange={setFace(0)} prefix="bulk-front" /></TabsContent>
          {multi && (
            <TabsContent value="back" className="space-y-5 pt-4">
              {dfc && (
                <div>
                  <span className="field-label">Back face image (choose from this upload)</span>
                  <ImagePool pool={pool} selected={faces[1].image_file_id} onSelect={(img) => setFace(1)({ image_file_id: img.file_id, image_url: img.url })} />
                </div>
              )}
              <FaceFields face={faces[1]} onChange={setFace(1)} prefix="bulk-back" />
            </TabsContent>
          )}
        </Tabs>
        <div className="mt-6 flex justify-end gap-3">
          <button className="btn-ghost" onClick={onClose} data-testid="bulk-details-cancel">Cancel</button>
          <button className="btn-gold" onClick={save} data-testid="bulk-details-save">Save details</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
